import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';
import { ROUTES } from '../../App';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useToast } from '../../hooks/use-toast';
import JobPostingForm from '../../components/practice/JobPostingForm';
import { geocodePostcode } from '../../utils/geolocationService';

const PracticeJobPostingPage = ({ user, currentUser, isEditing = false }) => {
  const navigate = useNavigate();
  const { jobId } = useParams(); // Get jobId from URL for editing
  const { theme } = useTheme();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [verifiedPractices, setVerifiedPractices] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [isLoadingJob, setIsLoadingJob] = useState(isEditing);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [practiceDetails, setPracticeDetails] = useState(null);

  // Extract verified practices from currentUser
  useEffect(() => {
    if (currentUser?.managedPractices) {
      const verified = currentUser.managedPractices.filter(
        practice => practice.verificationStatus === 'verified'
      );
      setVerifiedPractices(verified);
    }
  }, [currentUser]);

  // Fetch job data if editing
  useEffect(() => {
    const fetchJobData = async () => {
      if (!isEditing || !jobId) return;
      
      try {
        setIsLoadingJob(true);
        const jobDoc = await getDoc(doc(db, 'job_listings', jobId));
        
        if (!jobDoc.exists()) {
          toast({
            title: "Error",
            description: "Job listing not found",
            variant: "destructive"
          });
          navigate(ROUTES.PRACTICE_JOBS);
          return;
        }
        
        const jobData = jobDoc.data();
        
        // Security check - only allow editing jobs posted by this user
        if (jobData.postedByUid !== user?.uid) {
          toast({
            title: "Error",
            description: "You don't have permission to edit this job listing",
            variant: "destructive"
          });
          navigate(ROUTES.PRACTICE_JOBS);
          return;
        }
        
        setInitialData(jobData);
        
        // Automatically select the practice when editing
        if (jobData.managedPracticeId) {
          handlePracticeChange(jobData.managedPracticeId);
        }
      } catch (error) {
        console.error("Error fetching job data:", error);
        toast({
          title: "Error",
          description: "Failed to load job data",
          variant: "destructive"
        });
      } finally {
        setIsLoadingJob(false);
      }
    };
    
    fetchJobData();
  }, [isEditing, jobId, user, navigate, toast]);
  
  // Add this function to handle practice selection
  const handlePracticeChange = (practiceId) => {
    setSelectedPractice(practiceId);
    
    const practice = verifiedPractices.find(p => p.id === practiceId);
    if (practice) {
      setPracticeDetails(practice);
    }
  };

  const handleJobSubmit = async (jobData) => {
    try {
      setLoading(true);

      // Get the selected practice details including coordinates
      const selectedPracticeDetails = currentUser?.managedPractices?.find(
        practice => practice.id === selectedPractice
      );

      // Geocode the practice postcode to get lat/long for the job
      let jobLatitude = null;
      let jobLongitude = null;

      if (selectedPracticeDetails?.practicePostcode) {
        try {
          console.log('Geocoding postcode for job:', selectedPracticeDetails.practicePostcode);
          const coordinates = await geocodePostcode(selectedPracticeDetails.practicePostcode);
          if (coordinates && coordinates.latitude && coordinates.longitude) {
            jobLatitude = coordinates.latitude;
            jobLongitude = coordinates.longitude;
            console.log('Job geocoding successful. Lat:', jobLatitude, 'Lon:', jobLongitude);
          } else {
            console.warn('Job geocoding failed or no results for postcode:', selectedPracticeDetails.practicePostcode);
          }
        } catch (geoError) {
          console.error('Error during job geocoding:', geoError);
          // Continue saving job even if geocoding fails
        }
      }

      const jobPayload = {
        ...jobData,
        postedByUid: user.uid,
        managedPracticeId: selectedPractice,
        practiceInfo: {
          id: selectedPractice,
          name: practiceDetails?.name || selectedPracticeDetails?.practiceName || 'Unknown Practice',
          postcode: practiceDetails?.postcode || selectedPracticeDetails?.practicePostcode || ''
        },
        // Include location and postcode for job card display
        location: jobData.location || selectedPracticeDetails?.practiceAddress || '',
        postcode: selectedPracticeDetails?.practicePostcode || '',
        // FIXED: Only include practiceLocation if we have valid coordinates
        ...(jobLatitude && jobLongitude && {
          practiceLocation: {
            latitude: jobLatitude,
            longitude: jobLongitude
          }
        }),
        postedAt: new Date(),
        updatedAt: new Date(),
        views: 0,
        expiresAt: jobData.expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        status: 'active'
      };

      // Log for debugging
      console.log('Job payload:', {
        practiceLocation: jobPayload.practiceLocation,
        selectedPracticeDetails,
        practiceDetails
      });

      if (isEditing && jobId) {
        // Update existing job
        await updateDoc(doc(db, 'job_listings', jobId), {
          ...jobPayload,
          updatedAt: new Date()
        });
      } else {
        // Create new job
        await addDoc(collection(db, 'job_listings'), jobPayload);
      }

      navigate(ROUTES.PRACTICE_DASHBOARD);
    } catch (error) {
      console.error('Error saving job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(isEditing ? `${ROUTES.PRACTICE_JOBS}/${jobId}` : ROUTES.PRACTICE_DASHBOARD);
  };

  if (isLoadingJob) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Improved Header Layout - Back button now above content */}
      <div className="flex flex-col gap-2">
        {/* Back Button - Now above the title */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(isEditing ? `${ROUTES.PRACTICE_JOBS}/${jobId}` : ROUTES.PRACTICE_DASHBOARD)}
          className={cn(
            "self-start mb-2",
            theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white'
          )}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to {isEditing ? 'Job Details' : 'Dashboard'}
        </Button>
        
        {/* Page Title - Now has full width */}
        <div>
          <h1 className={cn(
            "text-3xl font-bold tracking-tight",
            theme === 'light' ? 'text-purple-700' : 'text-white'
          )}>
            {isEditing ? 'Edit Job Listing' : 'Post New Job'}
          </h1>
          <p className={cn(
            "text-sm mt-1",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            {isEditing 
              ? 'Update your job listing details below'
              : 'Create a new job listing for one of your verified practices'
            }
          </p>
        </div>
      </div>

      {/* Check if user has verified practices */}
      {verifiedPractices.length === 0 ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className={cn(
              "border rounded-lg p-6",
              theme === 'light' 
                ? 'bg-amber-50 border-amber-200 text-amber-800' 
                : 'bg-amber-900/20 border-amber-800 text-amber-200'
            )}>
              <h3 className={cn(
                "text-lg font-medium mb-2",
                theme === 'light' ? 'text-amber-800' : 'text-amber-200'
              )}>
                No Verified Practices
              </h3>
              <p className={cn(
                "text-sm mb-4",
                theme === 'light' ? 'text-amber-700' : 'text-amber-300'
              )}>
                You need at least one verified practice before you can post job listings.
              </p>
              <Button
                onClick={() => navigate(ROUTES.PRACTICE_VERIFY)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Verify Your Practices
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <JobPostingForm
          verifiedPractices={verifiedPractices}
          onSubmit={handleJobSubmit}
          onCancel={handleCancel}
          loading={loading}
          initialData={initialData}
          selectedPracticeId={selectedPractice} // Pass the selected practice ID
        />
      )}
    </div>
  );
};

export default PracticeJobPostingPage;
